import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Reveal-on-scroll wrapper — used to fade/slide content as it enters the
 * viewport. Honours user's prefers-reduced-motion setting.
 *
 * @param {"up"|"down"|"left"|"right"|"fade"|"zoom"} direction
 * @param {number} delay - in seconds
 * @param {number} duration - in seconds (default 0.9)
 * @param {number} amount - intersection ratio threshold (default 0.18)
 * @param {boolean} once - trigger only once (default true)
 */
export const Reveal = ({
  children,
  direction = "up",
  delay = 0,
  duration = 0.9,
  amount = 0.18,
  once = true,
  className = "",
  testId,
}) => {
  const prefersReducedMotion = useReducedMotion();

  const variants = {
    hidden: prefersReducedMotion
      ? { opacity: 0 }
      : {
          opacity: 0,
          y: direction === "up" ? 32 : direction === "down" ? -32 : 0,
          x: direction === "left" ? 32 : direction === "right" ? -32 : 0,
          scale: direction === "zoom" ? 0.94 : 1,
          filter: "blur(6px)",
        },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      scale: 1,
      filter: "blur(0px)",
      transition: {
        duration: prefersReducedMotion ? 0.3 : duration,
        delay,
        ease: [0.22, 1, 0.36, 1], // expo-out — premium feel
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount }}
      variants={variants}
      className={className}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
};
